import { Box, Button, Card, styled, TextField, Typography } from '@mui/material';

import { Form, Formik } from 'formik';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useState } from 'react';
import * as Yup from 'yup';

import { Color, DeleteAccountStrings, FormValidationStrings, GlobalStrings, UserFormStrings } from '../../constants';
import { useRequestAccountDeletion } from '../../lib/user/hooks';

type DeleteAccountFormValues = {
  fullname: string;
  email: string;
  phone: string;
  reason: string;
};

const initialValues: DeleteAccountFormValues = {
  fullname: '',
  email: '',
  phone: '',
  reason: '',
};

// Support only needs enough to find the account, so accept any plausible number
// and normalise to E.164 when it parses cleanly.
const countDigits = (value: string) => value.replace(/\D/g, '').length;

const validationSchema = Yup.object({
  fullname: Yup.string().trim().required(FormValidationStrings.FULLNAME_REQUIRED),
  email: Yup.string().trim().email(UserFormStrings.USER_FORM_ERROR_EMAIL_INVALID).required(UserFormStrings.USER_FORM_ERROR_EMAIL_REQUIRED),
  phone: Yup.string()
    .trim()
    .required(FormValidationStrings.PHONE_REQUIRED)
    .test('is-plausible-phone', FormValidationStrings.INVALID_PHONE, (value) => {
      const digits = countDigits(value || '');
      return digits >= 7 && digits <= 15;
    }),
  reason: Yup.string().trim().required(FormValidationStrings.REASON_REQUIRED),
});

const DeleteAccount = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { mutate, isLoading } = useRequestAccountDeletion();

  const handleSubmit = (values: DeleteAccountFormValues) => {
    setErrorMessage('');
    mutate(
      {
        fullname: values.fullname.trim(),
        email: values.email.trim(),
        phone: parsePhoneNumberFromString(values.phone.trim(), 'US')?.number || values.phone.trim(),
        reason: values.reason.trim(),
      },
      {
        onSuccess: () => setIsSubmitted(true),
        onError: () => setErrorMessage(DeleteAccountStrings.ERROR),
      },
    );
  };

  return (
    <Container>
      <LogoContainer>
        <img src="/assets/images/logo.svg" alt={GlobalStrings.GLOBAL_PROJECT_NAME} />
      </LogoContainer>
      <StyledCard>
        {isSubmitted ? (
          <Box display="grid" gap={2}>
            <Typography variant="h6" fontWeight={700}>
              {DeleteAccountStrings.SUCCESS_TITLE}
            </Typography>
            <Typography variant="body2">{DeleteAccountStrings.SUCCESS_DESCRIPTION}</Typography>
          </Box>
        ) : (
          <Box display="grid" gap={3}>
            <Box display="grid" gap={1}>
              <Typography variant="h6" fontWeight={700}>
                {DeleteAccountStrings.TITLE}
              </Typography>
              <Typography variant="body2">{DeleteAccountStrings.DESCRIPTION}</Typography>
            </Box>

            <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnChange={false} onSubmit={handleSubmit}>
              {({ values, handleChange, errors, touched }) => (
                <Form>
                  <Box display="grid" gap={3}>
                    <FieldContainer>
                      <Label>{DeleteAccountStrings.FULL_NAME}</Label>
                      <TextField
                        name="fullname"
                        fullWidth
                        placeholder={DeleteAccountStrings.FULL_NAME_PLACEHOLDER}
                        value={values.fullname}
                        onChange={handleChange}
                        error={touched.fullname && !!errors.fullname}
                        helperText={touched.fullname && errors.fullname}
                      />
                    </FieldContainer>
                    <FieldContainer>
                      <Label>{DeleteAccountStrings.EMAIL}</Label>
                      <TextField
                        name="email"
                        type="email"
                        fullWidth
                        placeholder={DeleteAccountStrings.EMAIL_PLACEHOLDER}
                        value={values.email}
                        onChange={handleChange}
                        error={touched.email && !!errors.email}
                        helperText={touched.email && errors.email}
                      />
                    </FieldContainer>
                    <FieldContainer>
                      <Label>{DeleteAccountStrings.PHONE}</Label>
                      <TextField
                        name="phone"
                        type="tel"
                        fullWidth
                        placeholder={DeleteAccountStrings.PHONE_PLACEHOLDER}
                        value={values.phone}
                        onChange={handleChange}
                        error={touched.phone && !!errors.phone}
                        helperText={touched.phone && errors.phone}
                      />
                    </FieldContainer>
                    <FieldContainer>
                      <Label>{DeleteAccountStrings.REASON}</Label>
                      <TextField
                        name="reason"
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder={DeleteAccountStrings.REASON_PLACEHOLDER}
                        value={values.reason}
                        onChange={handleChange}
                        error={touched.reason && !!errors.reason}
                        helperText={touched.reason && errors.reason}
                      />
                    </FieldContainer>

                    {!!errorMessage && (
                      <Typography variant="body2" color="error">
                        {errorMessage}
                      </Typography>
                    )}

                    <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
                      {isLoading ? DeleteAccountStrings.SUBMITTING : DeleteAccountStrings.SUBMIT}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        )}

        <Box display="grid" gap={2} mt={4}>
          <Typography variant="subtitle2" fontWeight={700}>
            {DeleteAccountStrings.WHAT_HAPPENS_TITLE}
          </Typography>

          <Box display="grid" gap={1}>
            <Typography variant="body2" fontWeight={600}>
              {DeleteAccountStrings.DELETED_TITLE}
            </Typography>
            <BulletList>
              <li>{DeleteAccountStrings.DELETED_CONTACT}</li>
              <li>{DeleteAccountStrings.DELETED_PROFILE}</li>
              <li>{DeleteAccountStrings.DELETED_PAYMENT}</li>
            </BulletList>
          </Box>

          <Box display="grid" gap={1}>
            <Typography variant="body2" fontWeight={600}>
              {DeleteAccountStrings.RETAINED_TITLE}
            </Typography>
            <BulletList>
              <li>{DeleteAccountStrings.RETAINED_ACTIVITY}</li>
              <li>{DeleteAccountStrings.RETAINED_RECORDS}</li>
            </BulletList>
          </Box>

          <Typography variant="body2">{DeleteAccountStrings.WHAT_HAPPENS_TIMING}</Typography>
        </Box>

        <Typography variant="body2" mt={3}>
          {DeleteAccountStrings.SUPPORT_PREFIX}
          <a href={`mailto:${DeleteAccountStrings.SUPPORT_EMAIL}`}>{DeleteAccountStrings.SUPPORT_EMAIL}</a>
        </Typography>
      </StyledCard>
    </Container>
  );
};

export default DeleteAccount;

const Container = styled('div')({
  background: Color.cultured,
  minHeight: '100vh',
  padding: '24px',
});

const LogoContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  marginBottom: 18,
});

const StyledCard = styled(Card)({
  maxWidth: 560,
  margin: '0 auto',
  padding: 24,
});

const FieldContainer = styled(Box)({
  display: 'grid',
  gap: 8,
});

const Label = styled(Typography)({
  fontWeight: 600,
  fontSize: 14,
});

const BulletList = styled('ul')({
  margin: 0,
  paddingLeft: 20,
  fontSize: 14,
  lineHeight: 1.5,
  '& li': {
    marginBottom: 4,
  },
});
